// src/components/popups/ProductShareDrawer.js
import React, { useEffect, useState } from 'react';
import './ProductShareDrawer.css';
import api from '../../api';

import profilePicPlaceholder from '../../assets/placeholder-profile.png';
import repostIcon from '../../assets/repost-icon.png';

function ProductShareDrawer({ isOpen, onClose, product, sender, onShared }) {
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loadingSuggested, setLoadingSuggested] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);

  const [repostText, setRepostText] = useState('');
  const [reposting, setReposting] = useState(false);
  const [senderProfile, setSenderProfile] = useState(null);

  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchSuggestedUsers();
      fetchSenderProfile();
    } else {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUsers([]);
      setSending(false);

      setRepostText('');
      setReposting(false);
      setSenderProfile(null);

      setSuccessMsg('');
      setLoadingSuggested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const body = document.body;

    if (isOpen) {
      const prevOverflow = body.style.overflow;

      body.style.overflow = 'hidden';
      body.classList.add('bf-product-share-drawer-open');

      return () => {
        body.style.overflow = prevOverflow;
        body.classList.remove('bf-product-share-drawer-open');
      };
    }
  }, [isOpen]);

  const fetchSuggestedUsers = async () => {
    try {
      setLoadingSuggested(true);

      const res = await api.get('/users/suggested');
      setSuggestedUsers(res.data || []);
    } catch (err) {
      console.error('❌ Error fetching suggested users:', err);
      setSuggestedUsers([]);
    } finally {
      setLoadingSuggested(false);
    }
  };

  const fetchSenderProfile = async () => {
    if (!sender) return;

    try {
      const res = await api.get(`/users/${encodeURIComponent(sender)}`);
      setSenderProfile(res.data || null);
    } catch (err) {
      console.error('❌ Error fetching sender profile:', err);
      setSenderProfile(null);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (query.length > 1) {
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(query)}`);
        setSearchResults(res.data || []);
      } catch (err) {
        console.error('❌ Search error:', err);
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  };

  const toggleSelect = (user) => {
    const already = selectedUsers.find((u) => u._id === user._id);

    if (already) {
      setSelectedUsers(selectedUsers.filter((u) => u._id !== user._id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const buildProductSnapshot = () => {
    if (!product) return null;

    return {
      _id: product._id,
      name: product.name,
      description: product.description,

      // DM product share can still keep price.
      // Product repost feed preview will NOT use price.
      price: product.price,
      usdPrice: product.usdPrice,
      pricingType: product.pricingType,

      media: Array.isArray(product.media) ? product.media : [],
      mediaType: product.mediaType || 'image',

      username: product.owner?.username || product.username || product.owner,
      ownerName: product.owner?.name || product.ownerName || product.nameOfOwner,
      profilePic: product.owner?.profilePic || product.profilePic,

      storeName: product.owner?.storeName || product.storeName,
      businessUsername: product.owner?.businessUsername || product.businessUsername,
      businessLogo: product.owner?.businessLogo || product.businessLogo,

      timestamp: product.createdAt || product.timestamp || new Date().toISOString(),
    };
  };

  const handleRepost = async () => {
    if (!product?._id || !sender || reposting || successMsg) return;

    try {
      setReposting(true);

      const res = await api.post(`/products/${product._id}/repost`, {
        username: sender,
        comment: repostText || '',
      });

      const newPost = res.data?.post || res.data;

      window.dispatchEvent(
        new CustomEvent('post:created', { detail: { post: newPost } })
      );

      onShared?.(newPost);

      setSuccessMsg('Product reposted');

      setTimeout(() => {
        onClose?.();
      }, 1200);
    } catch (err) {
      console.error('❌ Product repost failed:', err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to repost product.';

      alert(msg);
    } finally {
      setReposting(false);
    }
  };

  const handleSend = async () => {
    if (selectedUsers.length === 0 || sending || !sender) return;

    const productSnapshot = buildProductSnapshot();

    if (!productSnapshot?._id) {
      alert('No product to share.');
      return;
    }

    try {
      setSending(true);

      const tasks = selectedUsers.map((u) =>
        api.post('/messages', {
          from: sender,
          to: u.username,
          type: 'shared_product',
          product: productSnapshot,
        })
      );

      await Promise.all(tasks);

      setSuccessMsg('Sent!');
      onShared?.();

      setTimeout(() => {
        onClose?.();
      }, 900);
    } catch (err) {
      console.error('❌ Product share DM failed:', err);
      alert('Failed to share. Try again.');
    } finally {
      setSending(false);
    }
  };

  const renderUser = (user) => {
    const selected = selectedUsers.some((u) => u._id === user._id);

    return (
      <div
        key={user._id}
        className={`psd-user ${selected ? 'selected' : ''}`}
        onClick={() => toggleSelect(user)}
      >
        <img src={user.profilePic || profilePicPlaceholder} alt="pic" />

        <div className="psd-user-meta">
          <div className="psd-user-name">{user.name || user.username || 'Pioneer'}</div>
          <div className="psd-user-username">@{user.username}</div>
        </div>

        {selected && <div className="psd-check">✓</div>}
      </div>
    );
  };

  const renderUserSkeletons = () =>
    Array.from({ length: 6 }).map((_, index) => (
      <div
        key={`psd-user-skeleton-${index}`}
        className="psd-user psd-user-skeleton"
      >
        <div className="psd-user-skeleton-avatar" />
        <div className="psd-user-skeleton-lines">
          <div className="psd-user-skeleton-name" />
          <div className="psd-user-skeleton-username" />
        </div>
      </div>
    ));

  if (!isOpen) return null;

  const mediaUrl = product?.media?.[0] || null;

  const avatarSrc =
    senderProfile?.profilePic?.startsWith('http')
      ? senderProfile.profilePic
      : profilePicPlaceholder;

  const senderName = senderProfile?.name || sender || 'You';

  const isMerchant =
    senderProfile?.userType === 'merchant' &&
    (senderProfile?.isMerchant || senderProfile?.user?.isMerchant);

  const isAdmin =
    senderProfile?.isAdmin === true || !!senderProfile?.adminRole;

  const isApp =
    senderProfile?.isApp === true || senderProfile?.accountType === 'app';

  const roleLabel = isAdmin
    ? 'Admin'
    : isApp
    ? 'App'
    : isMerchant
    ? 'Merchant'
    : 'Pioneer';

  const roleClass = roleLabel === 'Pioneer' ? 'pioneer-pill' : 'merchant-pill';

  return (
    <div className="psd-backdrop" onClick={onClose}>
      <div className="psd-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="psd-header">
          <span>Share Product</span>

          <button className="psd-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>

        {product && (
          <div className="psd-product-strip">
            <div className="psd-product-thumb">
              {mediaUrl ? (
                <img src={mediaUrl} alt={product.name || 'Product'} />
              ) : (
                <div className="psd-thumb-empty">No media</div>
              )}
            </div>

            <div className="psd-product-text">
              <div className="psd-product-name">
                {product.name || 'Untitled product'}
              </div>

              <div className="psd-product-sub">
                {product.owner?.storeName ||
                  product.storeName ||
                  product.owner?.businessName ||
                  'BarterFi product'}
              </div>
            </div>
          </div>
        )}

        <div className="psd-repost-box">
          <div className="psd-repost-header">
            <img src={avatarSrc} alt="avatar" className="psd-repost-avatar" />

            <div className="psd-repost-meta">
              <div className="psd-repost-name">{senderName}</div>

              <div className="psd-repost-role-row">
                <span className={roleClass}>{roleLabel}</span>
              </div>
            </div>
          </div>

          <textarea
            className="psd-repost-input"
            placeholder="Say something..."
            value={repostText}
            onChange={(e) => setRepostText(e.target.value)}
            rows={3}
          />

          <button
            className="psd-repost-button"
            onClick={handleRepost}
            disabled={reposting || !sender || !product?._id || !!successMsg}
          >
            {successMsg === 'Product reposted' ? (
              'Done'
            ) : reposting ? (
              'Posting…'
            ) : (
              <>
                <img src={repostIcon} alt="" className="psd-repost-icon" />
                <span>Repost to Feed</span>
              </>
            )}
          </button>

          {successMsg === 'Product reposted' && (
            <div className="psd-success">{successMsg}</div>
          )}
        </div>

        <div className="psd-divider" />

        <div className="psd-section-title">Send DM</div>

        <input
          className="psd-search"
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />

        <div className="psd-user-list">
          {searchQuery.length > 1
            ? searchResults.map(renderUser)
            : loadingSuggested && suggestedUsers.length === 0
            ? renderUserSkeletons()
            : suggestedUsers.map(renderUser)}
        </div>

        {selectedUsers.length > 0 && (
          <button
            className="psd-send"
            onClick={handleSend}
            disabled={sending || successMsg === 'Sent!'}
          >
            {successMsg === 'Sent!'
              ? 'Done'
              : sending
              ? 'Sending…'
              : `Send to ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}`}
          </button>
        )}

        {successMsg === 'Sent!' && <div className="psd-success">{successMsg}</div>}
      </div>
    </div>
  );
}

export default ProductShareDrawer;
